/**
 * SignupPage — email + password signup with Google OAuth alternative.
 *
 * Frontend-only mock auth (see authStore). On success the user is
 * routed to /onboarding (first-run carousel), then /.
 */

import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/Button';
import { useAuthStore } from '@/stores/authStore';

export function SignupPage() {
  const { t } = useTranslation('auth');
  const navigate = useNavigate();
  const signupWithEmail = useAuthStore((s) => s.signupWithEmail);
  const loginWithGoogle = useAuthStore((s) => s.loginWithGoogle);

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);
    if (!name.trim()) return setError(t('errors.name_required'));
    if (!isValidEmail(email)) return setError(t('errors.email_invalid'));
    if (password.length < 8) return setError(t('errors.password_short'));
    setSubmitting(true);
    try {
      await signupWithEmail({ name, email, password });
      navigate('/onboarding', { replace: true });
    } catch (err) {
      const code = err instanceof Error ? err.message : 'generic';
      setError(t(`errors.${code}`, { defaultValue: t('errors.generic') }));
    } finally {
      setSubmitting(false);
    }
  };

  /** Triggers Supabase's OAuth redirect. The browser navigates away
   *  to Google's consent screen and lands back on `/auth/callback`. */
  const handleGoogle = async () => {
    setError(null);
    try {
      sessionStorage.setItem('gi-engine.post-login-redirect', '/onboarding');
      await loginWithGoogle();
    } catch {
      setError(t('errors.google_failed'));
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4" noValidate>
      <div className="text-center">
        <h2 className="text-lg font-semibold tracking-tight">{t('signup.heading')}</h2>
        <p className="mt-1 text-sm text-ink-muted">{t('signup.subheading')}</p>
      </div>

      <Field
        id="signup-name"
        label={t('signup.name_label')}
        type="text"
        value={name}
        onChange={setName}
        placeholder={t('signup.name_placeholder')}
        autoComplete="name"
        required
      />
      <Field
        id="signup-email"
        label={t('signup.email_label')}
        type="email"
        value={email}
        onChange={setEmail}
        placeholder={t('signup.email_placeholder')}
        autoComplete="email"
        required
      />
      <Field
        id="signup-password"
        label={t('signup.password_label')}
        type="password"
        value={password}
        onChange={setPassword}
        hint={t('signup.password_hint')}
        autoComplete="new-password"
        required
      />

      {error ? (
        <p role="alert" className="rounded-md bg-danger/10 px-3 py-2 text-sm text-danger">
          {error}
        </p>
      ) : null}

      <Button type="submit" className="w-full" size="lg" disabled={submitting}>
        {t('signup.submit')}
      </Button>

      <div className="flex items-center gap-3">
        <span className="h-px flex-1 bg-border" />
        <span className="text-xs uppercase tracking-wider text-ink-subtle">{t('signup.or')}</span>
        <span className="h-px flex-1 bg-border" />
      </div>

      <Button
        type="button"
        variant="secondary"
        size="lg"
        className="w-full"
        onClick={handleGoogle}
      >
        {t('signup.continue_with_google', { defaultValue: 'Continue with Google' })}
      </Button>

      <p className="text-center text-xs text-ink-subtle">{t('signup.agree_terms')}</p>

      <p className="text-center text-sm text-ink-muted">
        {t('signup.have_account')}{' '}
        <Link to="/login" className="font-medium text-spark hover:underline">
          {t('signup.login_link')}
        </Link>
      </p>
    </form>
  );
}

function Field({
  id,
  label,
  type,
  value,
  onChange,
  placeholder,
  autoComplete,
  required,
  hint,
}: {
  id: string;
  label: string;
  type: 'text' | 'email' | 'password';
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  autoComplete?: string;
  required?: boolean;
  hint?: string;
}) {
  return (
    <div>
      <label htmlFor={id} className="mb-1 block text-sm font-medium text-ink">
        {label}
      </label>
      <input
        id={id}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        autoComplete={autoComplete}
        required={required}
        className="block w-full rounded-md border border-border bg-canvas px-3 py-2 text-sm text-ink placeholder:text-ink-subtle focus:border-spark focus:outline-none"
      />
      {hint ? <p className="mt-1 text-xs text-ink-subtle">{hint}</p> : null}
    </div>
  );
}

function isValidEmail(email: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
}
